/**
 * Non-secret metadata for one admin connection. Authentication is deliberately
 * provider-scoped: the bearer session or break-glass key lives in a separate
 * secure slot, while this registry records only whether that slot is populated.
 */
export interface AdminProviderConnection {
  id: string;
  baseUrl: string;
  label: string;
  auth:
    | { kind: "session"; hasCredential: boolean }
    | { kind: "adminKey"; hasCredential: boolean };
  contractVersion?: string;
  addedAt: number;
}

export interface AdminProviderRegistry {
  connections: AdminProviderConnection[];
  activeId: string | null;
}

/**
 * Normalize an admin backend URL for identity comparisons. URL paths retain
 * their case; only the host is case-insensitive. Invalid/non-HTTP URLs return
 * null so callers can treat user-entered values as a no-op.
 */
export function normalizeAdminBaseUrl(url: string): string | null {
  if (typeof url !== "string") return null;
  const trimmed = url.trim().replace(/\/+$/, "");
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    if (
      (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
      !parsed.hostname
    ) {
      return null;
    }
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return null;
  }
}

/** Add a connection once per normalized base URL, preserving the first entry. */
export function addAdminProvider(
  registry: AdminProviderRegistry,
  connection: AdminProviderConnection,
): AdminProviderRegistry {
  const baseUrl = normalizeAdminBaseUrl(connection?.baseUrl);
  if (!baseUrl || !connection.id) return registry;

  const duplicate = registry.connections.some(
    (existing) => normalizeAdminBaseUrl(existing.baseUrl) === baseUrl,
  );
  if (duplicate) return registry;

  const normalized = { ...connection, baseUrl };
  return {
    connections: [...registry.connections, normalized],
    activeId: registry.activeId ?? normalized.id,
  };
}

/** Remove a connection; when it was active, select the first remaining one. */
export function removeAdminProvider(
  registry: AdminProviderRegistry,
  id: string,
): AdminProviderRegistry {
  if (!registry.connections.some((connection) => connection.id === id)) return registry;

  const connections = registry.connections.filter((connection) => connection.id !== id);
  return {
    connections,
    activeId: registry.activeId === id ? (connections[0]?.id ?? null) : registry.activeId,
  };
}

/** Select an existing connection. Unknown ids leave the registry unchanged. */
export function setActiveAdminProvider(
  registry: AdminProviderRegistry,
  id: string,
): AdminProviderRegistry {
  if (!registry.connections.some((connection) => connection.id === id)) return registry;
  if (registry.activeId === id) return registry;
  return { ...registry, activeId: id };
}

/** Resolve the active connection without ever manufacturing shared auth state. */
export function resolveActiveAdminProvider(
  registry: AdminProviderRegistry,
): AdminProviderConnection | null {
  if (registry.activeId === null) return null;
  return (
    registry.connections.find((connection) => connection.id === registry.activeId) ?? null
  );
}
